import React, { useState, useEffect } from "react";
import {
  Navigate,
  Route,
  Routes,
  Link,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import theme from "./theme";
import {
  AppBar,
  Toolbar,
  IconButton,
  Button,
  CssBaseline,
  Box,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import AccountCircle from "@mui/icons-material/AccountCircle";
import LogoutIcon from "@mui/icons-material/Logout";
import InstagramIcon from "@mui/icons-material/Instagram";
import { logEvent } from "firebase/analytics";
import { analytics } from "./firebaseConfig";
import { useMediaQuery } from "@mui/material";
import logo from "./HslLogo.png";

import Footer from "./components/Footer";
import NavDrawer from "./components/NavDrawer";
import HomeRouter from "./components/HomeRouter";
import BecomeAthlete from "./components/BecomeAthlete";
import RegisterFlow from "./components/RegisterFlow";
import RegisterSuccess from "./components/RegisterSuccess";
import SelectDivision from "./components/SelectDivision";
import PlayerProfile from "./components/PlayerProfile";
import RosterEditor from "./components/RosterEditor";
import UpdatePassword from "./components/UpdatePassword";
import YouthLeague from "./components/YouthLeague";
import MBLLeague from "./components/MBLLeague";
import Rules from "./components/Rules";
import Insights from "./components/Insights";
import TeamPage from "./components/TeamPage";
import Photos from "./components/Photos";
import Jobs from "./components/Jobs";
import { useAuth } from "./contexts/AuthContext";
import { LeagueRoute } from "./components/LeagueHub";

const drawerWidth = 240;

const usePageTracking = () => {
  const location = useLocation();
  useEffect(() => {
    logEvent(analytics, "page_view", { page_path: location.pathname });
  }, [location]);
};

const App = () => {
  usePageTracking();

  const { user, loading: authLoading, signOut } = useAuth();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const location = useLocation();

  const toggleDrawer = (open) => () => {
    setDrawerOpen(open);
    logEvent(analytics, "menu_toggled", { status: open ? "opened" : "closed" });
  };

  const isLanding = location.pathname === "/";
  const isRegisterFlow = location.pathname.startsWith("/register");
  const isAuthOrRegister = isLanding || isRegisterFlow;
  const isInsights = location.pathname === "/insights";
  const isFullBleed =
    isInsights ||
    ["/schedule", "/stats", "/standings", "/youth", "/mbl", "/rules", "/photos", "/jobs"].includes(location.pathname) ||
    location.pathname.startsWith("/team/") ||
    location.pathname.startsWith("/jobs/");

  /** Sign in: app bar only, home page, signed-out guests (not athletes on PlayerHome). */
  const showAppBarSignIn = isLanding && !authLoading && !user;
  const showProfileButton = !authLoading && user;
  const showGuestRegisterButton = !authLoading && !user && !isLanding && !isRegisterFlow;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          display: "flex",
          backgroundColor: theme.palette.background.default,
        }}
      >
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            zIndex: theme.zIndex.drawer + 1,
            backgroundColor: "#0a0a0a",
            width: "100%",
            borderBottom: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          <Toolbar
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              px: isMobile ? 1 : 2,
              position: "relative",
              minHeight: isMobile ? 56 : 64,
            }}
          >
            {isLanding ? (
              <IconButton
                edge="start"
                color="inherit"
                aria-label="menu"
                onClick={toggleDrawer(true)}
                sx={{ mr: 1, color: "#fff" }}
              >
                <MenuIcon />
              </IconButton>
            ) : (
              <IconButton
                edge="start"
                color="inherit"
                aria-label="menu"
                onClick={toggleDrawer(true)}
                sx={{ mr: 2, color: "#fff" }}
              >
                <MenuIcon />
              </IconButton>
            )}

            <Box
              component={Link}
              to="/"
              sx={{
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                textDecoration: "none",
              }}
            >
              <img
                src={logo}
                alt="HADAF Soccer League"
                style={{
                  height: isMobile ? "34px" : "46px",
                  maxWidth: "120px",
                  objectFit: "contain",
                }}
              />
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: isMobile ? 0.5 : 1 }}>
              {isLanding && !user && (
                <>
                  {showAppBarSignIn && (
                    <Button
                      component={Link}
                      to="/register?auth=1"
                      variant="outlined"
                      sx={{
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: isMobile ? 0.6 : 1,
                        fontSize: isMobile ? "0.55rem" : "0.8rem",
                        color: "#fff",
                        borderColor: "#fff",
                        borderWidth: isMobile ? 1 : 2,
                        borderRadius: 2,
                        px: isMobile ? 1 : 2.5,
                        py: isMobile ? 0.45 : 1,
                        minWidth: "auto",
                        whiteSpace: "nowrap",
                        "&:hover": {
                          borderColor: "#fff",
                          borderWidth: isMobile ? 1 : 2,
                          backgroundColor: "rgba(255,255,255,0.12)",
                        },
                      }}
                    >
                      Sign in
                    </Button>
                  )}
                  <Button
                    component={Link}
                      to="/register/select-division"
                    variant="contained"
                    sx={{
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: isMobile ? 0.6 : 1,
                        fontSize: isMobile ? "0.55rem" : "0.8rem",
                      backgroundColor: "#fff",
                      color: "#0a0a0a",
                      borderRadius: 2,
                      px: isMobile ? 1 : 2.5,
                      py: isMobile ? 0.45 : 1,
                      minWidth: "auto",
                      whiteSpace: "nowrap",
                      boxShadow: "none",
                      "&:hover": { backgroundColor: "#f0f0f0", boxShadow: "none" },
                    }}
                  >
                    Register
                  </Button>
                </>
              )}
              {showGuestRegisterButton && (
                <Button
                  component={Link}
                  to="/register/select-division"
                  variant="contained"
                  sx={{
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: isMobile ? 0.6 : 1,
                    fontSize: isMobile ? "0.55rem" : "0.8rem",
                    backgroundColor: "#fff",
                    color: "#0a0a0a",
                    borderRadius: 2,
                    px: isMobile ? 1 : 2.5,
                    py: isMobile ? 0.45 : 1,
                    minWidth: "auto",
                    whiteSpace: "nowrap",
                    boxShadow: "none",
                    "&:hover": { backgroundColor: "#f0f0f0", boxShadow: "none" },
                  }}
                >
                  Register
                </Button>
              )}
              {showProfileButton && (
                <IconButton
                  component={Link}
                  to="/profile"
                  aria-label="Your profile and stats"
                  sx={{
                    color: "#fff",
                    ml: 0.5,
                    "&:hover": { backgroundColor: "rgba(255,255,255,0.08)" },
                  }}
                >
                  <AccountCircle sx={{ fontSize: 28 }} />
                </IconButton>
              )}
              {showProfileButton && (
                <IconButton
                  onClick={async () => {
                    await signOut();
                    navigate("/", { replace: true });
                  }}
                  aria-label="Sign out"
                  sx={{
                    color: "#fff",
                    "&:hover": { backgroundColor: "rgba(255,255,255,0.08)" },
                  }}
                >
                  <LogoutIcon sx={{ fontSize: 26 }} />
                </IconButton>
              )}

              {/* Persistent socials */}
              <IconButton
                size={isMobile ? "small" : "medium"}
                component="a"
                href="https://instagram.com/hadafsoccerleague"
                target="_blank"
                rel="noreferrer"
                aria-label="Instagram"
                sx={{
                  color: "#fff",
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.08)" },
                  p: isMobile ? 0.5 : 1,
                }}
              >
                <InstagramIcon sx={{ fontSize: isMobile ? 22 : 26 }} />
              </IconButton>
            </Box>
          </Toolbar>
        </AppBar>

        <NavDrawer open={drawerOpen} onClose={toggleDrawer(false)} width={drawerWidth} />

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            minHeight: "100vh",
            transition: "margin 0.3s ease",
            // On mobile the Drawer overlays the content; don't shift layout.
            marginLeft: drawerOpen && !isMobile ? `${drawerWidth}px` : 0,
            backgroundColor: theme.palette.background.default,
            color: theme.palette.text.primary,
          }}
        >
          <Toolbar />
          <Box sx={{ flex: 1, minWidth: 0, p: isAuthOrRegister || isFullBleed ? 0 : 3 }}>
          <Routes>
            <Route path="/" element={<HomeRouter />} />
            <Route path="/register" element={<BecomeAthlete />} />
            <Route path="/register/select-division" element={<SelectDivision />} />
            <Route path="/register/apply" element={<RegisterFlow />} />
            <Route path="/register/success" element={<RegisterSuccess />} />
            <Route path="/update-password" element={<UpdatePassword />} />
            <Route path="/profile" element={<PlayerProfile />} />
            <Route path="/roster" element={<RosterEditor />} />
            <Route path="/schedule" element={<LeagueRoute section="fixtures" />} />
            <Route path="/stats" element={<LeagueRoute section="stats" />} />
            <Route path="/standings" element={<LeagueRoute section="standings" />} />
            <Route path="/insights" element={<Insights />} />
            <Route path="/team/:teamId" element={<TeamPage />} />
            <Route path="/rules" element={<Rules />} />
            <Route path="/photos" element={<Photos />} />
            <Route path="/jobs" element={<Jobs />} />
            <Route path="/jobs/:slug" element={<Jobs />} />
            <Route path="/youth" element={<YouthLeague />} />
            <Route path="/mbl" element={<MBLLeague />} />
            {/* Unknown or retired URL: send the player home instead of a blank page. */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Box>
          <Footer />
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default App;
